import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MatRadioChange } from '@angular/material/radio';
import { ESkeletonType } from '@norfolk-southern/accessns-components';
import { AgGridAngular } from 'ag-grid-angular';
import { addDays, differenceInDays } from 'date-fns';
import { Subscription } from 'rxjs';

import { NSCommonConstants, NSCommons } from '@ruby/configs/common.constants';
import { NSGuaranteesConstants } from '@ruby/configs/guarantees.constants';
import { IContact } from '@ruby/modules/account/models/contact.interface';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import { EStorageActions } from '@ruby/shared/enums/guarantees.enum';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import {
  IGuarantee,
  IGuaranteeEmail,
  IGuaranteeHistory,
  IGuaranteesConfirmation,
  IMultiGuaranteeRequest,
  IStorageGuarantee
} from '@ruby/shared/models/guarantees/guarantees.interface';
import { NSDateUtils } from '@ruby/shared/services/utils/ns-date.utils';

@Component({
  selector: 'ng-guarantees-storage-dialog',
  templateUrl: './guarantee-storage.dialog.html',
  styleUrls: ['./guarantee-storage.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuaranteeStorageDialog implements OnInit, OnChanges, OnDestroy {
  @Input() customer?: ICustomer;
  @Input() guarantee!: IGuarantee;
  @Input() userInfo?: IContact;
  @Input() guaranteeHistories!: Array<IGuaranteeHistory>;
  @Input() loading!: boolean;
  @Output() confirmGuarantee = new EventEmitter<IMultiGuaranteeRequest>();
  form!: UntypedFormGroup;
  title!: string;
  storageState = EStorageActions;
  guaranteeDays!: number;
  modified = false;
  guaranteeHistoryGridConfig: IGridConfigurations = NSGuaranteesConstants.guaranteeHistoryGridConfig;
  grid!: AgGridAngular;
  cancelled = false;
  disableCancelling = false;
  skeletonType: ESkeletonType = ESkeletonType.GRID;
  lastFreeDay!: Date;
  startAtDate!: Date;
  endAtDate!: Date;
  maxDate!: Date;
  disablePicker = true;
  maxReferenceNumberLimit = 30;
  separatorKeysCodes: Array<number> = [ENTER, COMMA];
  emails: Array<IGuaranteeEmail> = [];
  private subscription!: Subscription;

  constructor() { }

  ngOnInit(): void {
    this.title = NSGuaranteesConstants.confirmationTitle.storage;

    this.form = new UntypedFormGroup({
      email: new UntypedFormControl('', [Validators.pattern(NSCommonConstants.multipleEmailPattern), this.multipleEmailsValidator()]),
      reference: new UntypedFormControl('', Validators.required),
      guaranteeState: new UntypedFormControl('', Validators.required),
      startDate: new UntypedFormControl({
        value: '',
        disabled: true
      }, Validators.required),
      endDate: new UntypedFormControl({
        value: '',
        disabled: !(this.guarantee && this.guarantee.guaranteeType === EStorageActions.PARTIAL)
      }, Validators.required)
    });
    this.subscription = this.form.valueChanges.subscribe((value: IStorageGuarantee) => this.setModified(value));
    this.refreshGuarantee();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.guarantee?.currentValue) {
      this.refreshGuarantee();
    }

    if (changes.guaranteeHistories && changes.guaranteeHistories.currentValue.length > 0) {
      this.cancelled = changes.guaranteeHistories.currentValue[0].active === 'N';
      if (this.cancelled) {
        this.setupCancelled(changes.guaranteeHistories.currentValue[0]);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Refresh Guarantee
   *
   * @summary Set and refresh guarantee information in the form
   * @returns void
   */
  refreshGuarantee(): void {
    if (this.guarantee) {
      this.lastFreeDay = new Date(new Date(this.guarantee.lastFreeDateTime).getTime() - NSDateUtils.getNSTimezoneDiff());
      if (this.guarantee.guaranteed) {
        this.initDatePickerValues(false);
        this.disablePicker = this.guarantee.guaranteeType !== EStorageActions.PARTIAL;
      }
      this.form.patchValue({
        reference: this.guarantee.guaranteeRefNumber,
        guaranteeState: this.guarantee.guaranteed ? this.guarantee.guaranteeType : EStorageActions.FULL_GUARANTEE,
        startDate: this.startAtDate,
        endDate: this.endAtDate
      });
    }
  }

  /**
   * Set Grid
   *
   * @summary Set grid
   * @param $grid: AgGridAngular
   * @returns void
   */
  setGrid($grid: AgGridAngular): void {
    this.grid = $grid;
  }

  /** Data Rendered Grid
   *
   * @summary Select the first row once the grid was rendered
   * @returns void
   */
  dataRenderedGrid(): void {
    this.grid.api.getRowNode('0')!.selectThisNode(true);
  }

  /**
   * Storage Type Change
   *
   * @summary enable and disable guarantee days based on selection
   * @param $event: MatRadioChange
   * @returns void
   */
  storageStateChange($event: MatRadioChange): void {
    if ($event.value) {
      if ($event.value === EStorageActions.PARTIAL) {
        this.initDatePickerValues(true);
        this.form.controls.startDate.setValue(this.startAtDate);
        this.form.controls.startDate.addValidators(Validators.required);
        this.form.controls.startDate.updateValueAndValidity();
        this.form.controls.endDate.setValue(this.endAtDate);
        this.form.controls.endDate.enable();
        this.form.controls.endDate.addValidators(Validators.required);
        this.form.controls.endDate.updateValueAndValidity();
        this.disablePicker = false;
      } else {
        this.form.controls.startDate.setValue('');
        this.form.controls.startDate.removeValidators(Validators.required);
        this.form.controls.startDate.updateValueAndValidity();
        this.form.controls.endDate.disable();
        this.form.controls.endDate.setValue('');
        this.form.controls.endDate.removeValidators(Validators.required);
        this.form.controls.endDate.updateValueAndValidity();
        this.disablePicker = true;
      }
      this.disableCancelling = false;
    }
  }

  /**
   * Emit Confirm Guarantee Submit
   *
   * @summary Emits Confirm Guarantee Event with the right payload
   * @returns void
   */
  submit(): void {
    const guarantee: IGuaranteesConfirmation = {
      chargeType: 'S',
      corporateCustomerName: `${ this.customer!.customerName }`,
      email: this.form.get('email')!.value,
      equipment: this.guarantee.equipmentId,
      equipmentType: this.guarantee.type,
      guaranteeAmount: this.guarantee.storageAmount || '0.00',
      referenceNumber: this.form.get('reference')!.value,
      terminalName: this.guarantee.terminal,
      waybillSerialNumber: this.guarantee.waybillSerialNumber,
      guaranteeThroughDate: (this.form.get('endDate')!.value) ? this.form.get('endDate')!.value.toJSON() : undefined,
      corpPrimarySix: this.customer!.masterPrimarySix
    };
    this.setGuaranteeProps(guarantee);
    const payload = { guarantees: [guarantee], emails: this.emails.map(email => email.email) };
    this.confirmGuarantee.emit(payload);
  }

  /**
   * Opened Picker
   *
   * @summary Initialize the values for the picker
   * @returns void
   */
  openedPicker(): void {
    this.form.controls.startDate.setValue(this.startAtDate);
    this.form.controls.endDate.setValue('');
  }

  /**
   * Closed Picker
   *
   * @summary Calculate guarantee days once the picker is closed
   * @returns void
   */
  closedPicker(): void {
    this.form.controls.startDate.setValue(this.startAtDate);
    this.form.controls.endDate.setValue(this.endAtDate);
  }

  /**
   * Picker Start Changes
   *
   * @summary Prevent to change start Date
   * @param $event: Date
   * @returns void
   */
  pickerStartChanges($event?: Date): void {
    if ($event?.getDate() !== this.startAtDate.getDate()) {
      this.form.controls.startDate.setValue(this.startAtDate);
    }
  }

  /**
   * Picker End Changes
   *
   * @summary Set end date
   * @param $event: Date
   * @returns void
   */
  pickerEndChanges($event?: Date): void {
    this.guaranteeDays = Math.abs(differenceInDays(NSDateUtils.nsSetDate(this.startAtDate.getTime()), NSDateUtils.nsSetDate($event!.getTime()))) + 1;
    this.endAtDate = NSDateUtils.convertFromAnyTimezoneToNSDate($event!.getTime());
  }

  /**
   * Date Class
   *
   * @summary Determine the scss class for a Last Free Day or start Date
   * @param cellDate: Date
   * @returns MatCalendarCellCssClasses
   */
  dateClass(cellDate: Date): MatCalendarCellCssClasses {
    if (NSDateUtils.formatNSDate(this.lastFreeDay.getTime(), 'MM/dd/yyyy') ===
      NSDateUtils.formatNSDate(new Date(new Date(cellDate.getTime()).getTime() - NSDateUtils.getNSTimezoneDiff()), 'MM/dd/yyyy')) {
      return 'mat-calendar-body-storage-lfd';
    } else {
      if (NSDateUtils.formatNSDate(new Date(new Date(cellDate.getTime()).getTime() - NSDateUtils.getNSTimezoneDiff()), 'MM/dd/yyyy') ===
        NSDateUtils.formatNSDate(new Date(new Date(this.startAtDate.getTime()).getTime() - NSDateUtils.getNSTimezoneDiff()), 'MM/dd/yyyy')) {
        return 'mat-calendar-body-storage-start-date-selected';
      } else {
        return 'mat-calendar-body-storage';
      }
    }
  }

  /**
   * Add email chips to the form
   *
   * @summary Add email to be notified over the guarantee recipients, this function will validate if the email is valid or not, and do not allow to add more than 10 emails
   * @param event string
   * @returns void
   */
  addEmail(event: string = ''): void {
    const emails = NSCommons.transformEmailsStringToArray((event).trim());
    emails.forEach((value: string) => {
      const email: IGuaranteeEmail = { email: value, invalid: !value.match(NSCommonConstants.multipleEmailPattern) };
      if (value && this.emails.length < 10 && this.emails.map(e => e.email).indexOf(value) === -1) {
        this.emails.push(email);
      }
    });
    this.form.controls.email.reset();
  }

  /**
   * Removes one email from chips component
   *
   * @summary Remove emails from the recipients list, it uses index as parameter input
   * @param index
   * @returns void
   */
  removeEmail(index: number): void {
    if (index >= 0) {
      this.emails.splice(index, 1);
      this.form.controls.email.reset();
    }
  }

  private multipleEmailsValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      let result = {};
      if (this.emails.some((email: IGuaranteeEmail) => email.invalid)) {
        result = { invalidEmails: true };
      }
      return result;
    };
  }

  private initDatePickerValues(stateChanged: boolean): void {
    this.startAtDate = addDays(this.lastFreeDay.getTime(), 1);
    if (this.guarantee.guaranteeDays > 0) {
      this.endAtDate = NSDateUtils.convertFromAnyTimezoneToNSDate(addDays(this.lastFreeDay.getTime(), this.guarantee.guaranteeDays));
    } else {
      this.endAtDate = NSDateUtils.convertFromAnyTimezoneToNSDate(this.startAtDate.getTime());
    }
    this.maxDate = NSDateUtils.convertFromAnyTimezoneToNSDate(addDays(this.lastFreeDay.getTime(), 99));
    if (stateChanged) {
      this.guaranteeDays = Math.abs(differenceInDays(this.startAtDate.getTime(), this.endAtDate.getTime())) + 1;
    } else {
      this.guaranteeDays = this.guarantee.guaranteeDays;
    }
  }

  private setGuaranteeProps(payload: IGuaranteesConfirmation): void {
    const state = this.form.get('guaranteeState')!.value;
    const days = this.guaranteeDays;
    payload.guaranteeDays = state === EStorageActions.CANCEL ? 0 : state === EStorageActions.PARTIAL ? days : 1;
    if (this.guarantee.guaranteed) {
      payload.guaranteeType = state === EStorageActions.CANCEL ? this.guarantee.guaranteeType : state;
    } else {
      payload.guaranteeType = state;
    }
  }

  private setModified(value: IStorageGuarantee): void {
    if (this.guarantee.guaranteed || this.cancelled) {
      this.modified = this.guarantee.guaranteeType !== value.guaranteeState ||
        this.guarantee.guaranteeRefNumber !== value.reference ||
        (this.guarantee.guaranteeType === EStorageActions.PARTIAL && this.guarantee.guaranteeDays !== Math.abs(differenceInDays(this.startAtDate, value.endDate)) + 1);
    }
  }

  private setupCancelled(guaranteeHistory: IGuaranteeHistory): void {

    this.form.controls.guaranteeState.setValue(EStorageActions.CANCEL);
    this.form.controls.reference.setValue(guaranteeHistory.referenceNumber.trim());
    this.form.controls.guaranteeState.updateValueAndValidity();
    this.form.controls.reference.updateValueAndValidity();

    this.guarantee = {
      ...this.guarantee,
      guaranteeType: EStorageActions.CANCEL,
      guaranteeRefNumber: guaranteeHistory.referenceNumber.trim(),
      guaranteeDays: isNaN(Number(guaranteeHistory.guaranteeDays)) ? 0 : Number(guaranteeHistory.guaranteeDays)
    };

    this.disableCancelling = true;
  }
}
